import dateFns from 'date-fns';
import _ from 'lodash';
import Markdown from 'markdown-to-jsx';
import React, { useContext, useState } from 'react';
import { MessagingContext } from '../../../services';
import { BaseInput } from '../../base';
import { AttachmentButton } from '../attachment-button';
import { SearchInput } from '../compose-panel';
import { NoteIndicator } from '../note-indicator';
import * as S from './chat-body.styles';
import {BlockedComponent} from "./blocked-component";

interface IChatBody {
    hideNote?: boolean;
}

export const ChatBody: React.FunctionComponent<IChatBody> = (props: IChatBody): React.ReactElement => {
    const [isEditable, setEditable] = useState(false);
    const [isSubjectLoading, setSubjectLoading] = useState(false);

    const { hideNote } = props;
    const fmtDate = (d: Date): string => dateFns.format(d, 'MMM D, YYYY');
    const parse = (d: string | undefined): Date => (d ? dateFns.parse(d) : new Date());

    const context = useContext(MessagingContext);
    const comment = _.first(context.comments);
    const isReadOnlyClaimChoice = context.isReadOnlyClaimChoice;
    if (!context.message || !comment) {
        return <></>;
    }

    // NB: test both ProviderComment and SupportComment, IFF not hiding notes
    const hasNote = !hideNote && _.get(comment, 'note', false);

    const { subject, claimId, created, id } = context.message;
    const [subjectView, setSubjectView] = useState(subject);

    const documents = _.filter(context.documents, document => {
        const commentDocs = _.get(comment, 'documents', []);
        return _.includes(commentDocs, document.id);
    });

    const renderAttachments = (): React.ReactElement[] | undefined => {
        if (!documents || !documents.length) {
            return;
        }
        return _.times(
            _.size(documents),
            (i: number): React.ReactElement => {
                const document = documents[i];
                const onDownload = async (): Promise<void> => {
                    if (document && document.id) {
                        await (context.onDocumentDownload && context.onDocumentDownload(document.id));
                    }
                };

                const onAssign = async (): Promise<void> => {
                    await (context.onDocumentAssign && context.onDocumentAssign(document));
                };

                const canAssign = context.message?.claimId && !document.claimId && !!context.onDocumentAssign;

                return (
                    <AttachmentButton
                        key={`attachment-${i}`}
                        filename={document.filename as string}
                        filesize={document.filesize}
                        thumbnail={document.thumbnail}
                        onDownload={onDownload}
                        onAssign={canAssign ? onAssign : undefined}
                    />
                );
            },
        );
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSubjectView(e.target.value);
    };

    const handleBlur = async (e: React.FocusEvent<HTMLInputElement>) => {
        setSubjectView(e.target.value);
        if (!id) {
            throw new Error("Can't edit subject for this message");
        }
        setSubjectLoading(true);
        await context.onSubjectUpdate(id, e.target.value);
        setSubjectLoading(false);
        handleToggleEditable();
    };

    const handleToggleEditable = () => {
        if (!context.isSubjectEditable) {
            return;
        }

        setEditable(!isEditable);
    };

    const handleRemoveClaim = () => {
        if (!id) {
            throw new Error("Can't edit claim for this message");
        }

        context.onClaimUpdate(id, null);
    };

    const handleChangeClaim = (claimId: string) => {
        if (!id) {
            throw new Error("Can't edit claim for this message");
        }

        context.onClaimUpdate(id, claimId);
    };

    return (
        <S.Header note={hasNote}>
            <S.Row>
                <S.Timestamp>{fmtDate(parse(created))}</S.Timestamp>
                {!hideNote && <NoteIndicator comment={comment} />}
            </S.Row>
            {/* <S.Topic>{patientFirst} {datesOfService}</S.Topic> */}
            {isEditable ? (
                <S.EditableSubject title="Edit">
                    <BaseInput
                        onChange={handleChange}
                        disabled={isSubjectLoading}
                        onBlur={handleBlur}
                        value={subjectView}
                        autoFocus={true}
                    />
                </S.EditableSubject>
            ) : (
                <S.Subject onClick={handleToggleEditable}>{subjectView}</S.Subject>
            )}
            <S.ClaimWrapper>
                {claimId ? (
                    <S.ClaimButton claimId={claimId} onRemove={handleRemoveClaim} forceClaim={!!isReadOnlyClaimChoice} />
                ) : (
                    (!!isReadOnlyClaimChoice ? <></> : <SearchInput onClaimSelected={handleChangeClaim} disabled={false} />)
                )}
            </S.ClaimWrapper>
            <S.Body>
                <Markdown options={{
                    overrides: {
                        iframe: {
                            component: BlockedComponent
                        },
                    },
                }}>{comment.body}</Markdown>
            </S.Body>
            <S.Attachments>{renderAttachments()}</S.Attachments>
        </S.Header>
    );
};
